import { ReactNode } from 'react';
import {
  CallRouteExpansion,
  PhoneHoursScheduleRule,
  WeaveAssistantExpansion,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import {
  CallGroupExpansion,
  CallObject,
  CallQueueExpansion,
  DeviceExpansion,
  ForwardingNumberExpansion,
  VoicemailExpansion,
  PhoneHoursExpansion,
  PlayMessageExpansion,
  PhoneTreeExpansion,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { NodeProps, Node as ReactFlowNode } from '@xyflow/react';
import { CallRouteV1 } from '@frontend/api-call-route';

export const NodeTypeLabels: Record<string, string> = {
  [CallRouteV1.Types.NodeType.NODE_TYPE_START]: 'Start',
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE]: 'Forward Device',
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER]: 'Forward Number',
  [CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX]: 'Voicemail',
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP]: 'Call Group',
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE]: 'Call Queue',
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE]: 'Call Route',
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE]: 'Phone Tree',
  [CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT]: 'Repeat',
  [CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION]: 'Tree Option',
  [CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE]: 'Play Message',
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS]: 'Phone Hours',
  [CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS]: 'Open Phone Hours',
  [CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS]: 'Closed Phone Hours',
  [CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS]: 'Break Phone Hours',
  [CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK]: 'Fallback',
  [CallRouteV1.Types.NodeType.NODE_TYPE_UNSPECIFIED]: 'Unspecified',
  [CallRouteV1.Types.NodeType.NODE_TYPE_BOOLEAN]: 'Boolean',
  [CallRouteV1.Types.NodeType.NODE_TYPE_TERMINATE]: 'Terminate',
  [CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION]: 'Device Route',
  [CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT]: 'Weave Assistant',
} as const;

export const extendableNodes: CallRouteV1.Types.NodeType[] = [
  CallRouteV1.Types.NodeType.NODE_TYPE_START,
  CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION,
  CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS,
  CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS,
  CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS,
  CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP,
  CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE,
  CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE,
  CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER,
  CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK,
  CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE,
] as const;

export const associativeNodes = [
  CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE,
  CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS,
  CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT,
];

// This is the type for the custom data added to the nodes passed to the graph.
export type DataPayload = {
  id: string;
  label: string;
  warning: string;
  callObject?: CallObject;
  isExtendable: boolean;
  isPhoneTreeDescendent?: boolean;
  parentNodeType?: CallRouteV1.Types.NodeType;
};

// This type is used to define the props that are passed to the nodes in the graph. It is meant
// to be a shared type that can be used for all custom nodes.
export type SharedNodeProps = NodeProps<ReactFlowNode<DataPayload, CallRouteV1.Types.NodeType>> & CustomNodeProps;

export type CustomNodeProps = {
  canEdit?: boolean;
  hoursManagementPermission?: boolean;
  isDeviceExtensionRoute?: boolean;
  onCallObjectLinkClick?: (data: { callObjectId: string; nodeId: string; type: CallRouteV1.Types.NodeType }) => void;
  onEditIconClick?: (data: { nodeId: string }) => void;
  onDeleteIconClick?: (data: { nodeId: string }) => void;
  AudioPlayerComponent?: (props: { mediaItemId: string; downloadUrl?: string }) => ReactNode;
  DayHoursListComponent?: (props: { phoneHours: PhoneHoursScheduleRule[] }) => ReactNode;
};

// Type guards for each expansion type
export function hasCallGroupExpansion(
  obj?: CallObject
): obj is CallObject & { callGroupExpansion: CallGroupExpansion } {
  return obj !== undefined && 'callGroupExpansion' in obj;
}

export function hasCallQueueExpansion(
  obj?: CallObject
): obj is CallObject & { callQueueExpansion: CallQueueExpansion } {
  return obj !== undefined && 'callQueueExpansion' in obj;
}

export function hasCallRouteExpansion(
  obj?: CallObject
): obj is CallObject & { callRouteExpansion: CallRouteExpansion } {
  return obj !== undefined && 'callRouteExpansion' in obj;
}

export function hasDeviceExpansion(obj?: CallObject): obj is CallObject & { deviceExpansion: DeviceExpansion } {
  return obj !== undefined && 'deviceExpansion' in obj;
}

export function hasForwardingNumberExpansion(
  obj?: CallObject
): obj is CallObject & { forwardingNumberExpansion: ForwardingNumberExpansion } {
  return obj !== undefined && 'forwardingNumberExpansion' in obj;
}

export function hasVoicemailExpansion(
  obj?: CallObject
): obj is CallObject & { voicemailExpansion: VoicemailExpansion } {
  return obj !== undefined && 'voicemailExpansion' in obj;
}

export function hasPhoneHoursExpansion(
  obj?: CallObject
): obj is CallObject & { phoneHoursExpansion: PhoneHoursExpansion } {
  return obj !== undefined && 'phoneHoursExpansion' in obj;
}

export function hasPlayMessageExpansion(
  obj?: CallObject
): obj is CallObject & { playMessageExpansion: PlayMessageExpansion } {
  return obj !== undefined && 'playMessageExpansion' in obj;
}

export function hasPhoneTreeExpansion(
  obj?: CallObject
): obj is CallObject & { phoneTreeExpansion: PhoneTreeExpansion } {
  return obj !== undefined && 'phoneTreeExpansion' in obj;
}

export function hasWeaveAssistantExpansion(
  obj?: CallObject
): obj is CallObject & { weaveAssistantExpansion: WeaveAssistantExpansion } {
  return obj !== undefined && 'weaveAssistantExpansion' in obj;
}
